import { Injectable } from '@angular/core';

import { STATES } from '../../constants/states.constant';
import { environment } from '../../../environments/environment';
import { HereGeocodeResult } from '../here/here.service';
import { TelemetryService } from '../telemetry/telemetry.service';

export const ADDRESS_AUTOCOMPLETE_MIN_LENGTH = 3;

type AddressTelemetryLabels = {
  flow: string;
  query_length?: number;
  suggestions_count?: number;
  has_city?: boolean;
  has_state?: boolean;
  has_zip_code?: boolean;
};

type AddressPatchOptions = {
  stateFormat?: 'raw' | 'fullName';
  zipFieldName?: string;
  labelFieldName?: string;
  latitudeFieldName?: string;
  longitudeFieldName?: string;
};

@Injectable({ providedIn: 'root' })
export class AddressAutocompleteService {
  constructor(private readonly telemetryService: TelemetryService) {}

  public getQueryValue(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    if (!value || typeof value !== 'object') {
      return '';
    }

    const selection = value as HereGeocodeResult;
    return selection.address || selection.label || '';
  }

  public getDisplayValue(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    if (!value || typeof value !== 'object') {
      return '';
    }

    const selection = value as HereGeocodeResult;
    return selection.label || selection.address || '';
  }

  public normalizeQuery(query: string): string {
    return (query || '').trim();
  }

  public hasMinimumQueryLength(
    query: string,
    minLength: number = ADDRESS_AUTOCOMPLETE_MIN_LENGTH,
  ): boolean {
    return this.normalizeQuery(query).length >= minLength;
  }

  public buildSelectionPatch(
    selection: HereGeocodeResult | null | undefined,
    options: AddressPatchOptions = {},
  ): Record<string, string> {
    const {
      stateFormat = 'raw',
      zipFieldName = 'zipCode',
      labelFieldName,
      latitudeFieldName,
      longitudeFieldName,
    } = options;

    const patch: Record<string, string> = {
      address: this.getStreetAddressValue(selection),
      city: selection && selection.city || '',
      state: this.normalizeState(selection && selection.state || '', stateFormat),
      [zipFieldName]: selection && selection.zipCode || '',
    };

    if (labelFieldName) {
      patch[labelFieldName] = this.getDisplayValue(selection);
    }

    if (latitudeFieldName) {
      patch[latitudeFieldName] = selection && selection.lat || '';
    }

    if (longitudeFieldName) {
      patch[longitudeFieldName] = selection && selection.lon || '';
    }

    return patch;
  }

  public getStreetAddressValue(selection: HereGeocodeResult | null | undefined): string {
    if (!selection) {
      return '';
    }

    const houseNumber = (selection.houseNumber || '').trim();
    const street = (selection.street || '').trim();
    if (houseNumber && street) {
      return `${houseNumber} ${street}`;
    }

    return (selection.address || '').replace(/^(\d+[A-Za-z0-9-]*),\s+/, '$1 ').trim();
  }

  public trackTyped(flow: string, query: string) {
    const sanitizedQuery = (query || '').trim();

    if (!sanitizedQuery.length) {
      return;
    }

    this.capture('address_autocomplete_typed', {
      flow,
      query_length: sanitizedQuery.length,
    });
  }

  public trackSuggestionsShown(flow: string, query: string, suggestionsCount: number) {
    const sanitizedQuery = (query || '').trim();

    if (!sanitizedQuery.length) {
      return;
    }

    this.capture('address_autocomplete_suggestions_shown', {
      flow,
      query_length: sanitizedQuery.length,
      suggestions_count: suggestionsCount,
    });
  }

  public trackSelected(flow: string, selection: HereGeocodeResult | null | undefined) {
    if (!selection) {
      return;
    }

    this.capture('address_autocomplete_selected', {
      flow,
      has_city: !!selection.city,
      has_state: !!selection.state,
      has_zip_code: !!selection.zipCode,
    });
  }

  public trackSubmitted(flow: string) {
    this.capture('address_autocomplete_form_submitted', { flow });
  }

  private normalizeState(state: string, stateFormat: 'raw' | 'fullName'): string {
    const sanitizedState = (state || '').trim();

    if (!sanitizedState.length || stateFormat === 'raw') {
      return sanitizedState;
    }

    const matchedState = STATES.find((stateOption) =>
      stateOption.name.toLowerCase() === sanitizedState.toLowerCase()
      || stateOption.abbreviation.toLowerCase() === sanitizedState.toLowerCase(),
    );

    return matchedState && matchedState.name || sanitizedState;
  }

  private capture(name: string, labels: AddressTelemetryLabels) {
    if (!environment.addressAutocompleteTelemetryEnabled) {
      return;
    }

    this.telemetryService.captureEvent(name, labels);
  }
}
